import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { NotificationProps, NotificationContextProps } from './NotificationTypes';

// Tạo context
const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

// Hook để sử dụng notification context
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

// Provider component
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationProps[]>([]);
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const isMountedRef = useRef(true);

  // Cleanup function
  const cleanup = useCallback(() => {
    timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    timeoutsRef.current.clear();
  }, []);

  // Setup and cleanup on mount/unmount
  useEffect(() => {
    // Đảm bảo isMountedRef được set thành true khi component mount
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  // Thêm thông báo mới
  const addNotification = useCallback((notification: Omit<NotificationProps, 'id'>) => {
    // Kiểm tra xem component có đang được mount không
    if (!isMountedRef.current) {
      console.warn('NotificationProvider is not mounted. Cannot add notification.');
      return '';
    }

    const id = uuidv4();
    const newNotification: NotificationProps = {
      ...notification,
      id,
      timestamp: Date.now(),
      duration: notification.duration || 3000,
      autoClose: notification.autoClose !== false
    };

    setNotifications((prev) => {
      // Kiểm tra lại mounted state trong setState callback
      if (!isMountedRef.current) return prev;

      // Kiểm tra xem thông báo tương tự đã tồn tại chưa (để tránh spam)
      const isDuplicate = prev.some((n) => n.title === notification.title && n.message === notification.message && n.type === notification.type);

      // Nếu đã có thông báo tương tự, không thêm nữa
      if (isDuplicate) return prev;

      // Giới hạn số lượng thông báo hiển thị cùng lúc (tối đa 5)
      const updatedNotifications = [...prev, newNotification];
      if (updatedNotifications.length > 5) {
        return updatedNotifications.slice(-5); // Giữ lại 5 thông báo mới nhất
      }

      return updatedNotifications;
    });

    // Tự động xóa thông báo sau thời gian đã định
    if (newNotification.autoClose) {
      const timeout = setTimeout(() => {
        if (isMountedRef.current) {
          removeNotification(id);
        }
        timeoutsRef.current.delete(id);
      }, newNotification.duration);

      timeoutsRef.current.set(id, timeout);
    }

    return id;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- removeNotification defined below to avoid circular dep; uses ref for mounted check
  }, []);

  // Xóa một thông báo
  const removeNotification = useCallback((id: string) => {
    if (!isMountedRef.current) {
      console.warn('NotificationProvider is not mounted. Cannot remove notification.');
      return;
    }

    // Clear timeout if exists
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }

    setNotifications((prev) => {
      // Kiểm tra lại mounted state trong setState callback
      if (!isMountedRef.current) return prev;

      const notification = prev.find((n) => n.id === id);
      if (notification?.onClose) {
        try {
          notification.onClose();
        } catch (error) {
          console.error('Error in notification onClose callback:', error);
        }
      }
      return prev.filter((n) => n.id !== id);
    });
  }, []);

  // Xóa tất cả thông báo
  const clearAll = useCallback(() => {
    if (!isMountedRef.current) {
      console.warn('NotificationProvider is not mounted. Cannot clear notifications.');
      return;
    }

    cleanup();
    setNotifications((prev) => {
      // Kiểm tra lại mounted state trong setState callback
      if (!isMountedRef.current) return prev;
      return [];
    });
  }, [cleanup]);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAll
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};
